import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Search, X, Send, Settings, HelpCircle, LogOut, CreditCard, Sparkles, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GreenLightLogo } from "@/components/GreenLightLogo";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";

const contactSchema = z.object({
  subject: z.string().trim().min(1, "Subject is required").max(200, "Subject must be less than 200 characters"),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters"),
});

interface HelpTopic {
  id: string;
  category: string;
  title: string;
  content: string;
}

const helpTopics: HelpTopic[] = [
  {
    id: "create-project",
    category: "Getting Started",
    title: "Creating your first project",
    content: "Click the \"+ New Project\" card on your dashboard to create a new project. Give it a descriptive name (e.g., \"Summer Campaign Newsletter\"). Once created, you'll receive a unique email address for that project where you can forward email designs for review.",
  },
  {
    id: "dashboard-overview",
    category: "Getting Started",
    title: "Understanding the dashboard",
    content: "Your dashboard displays all active projects as cards. Each card shows the project name, a thumbnail preview of the latest email version, comment count, and last activity date. At the bottom of the page, you'll find your archived projects which can be restored at any time.",
  },
  {
    id: "forwarding-emails",
    category: "Email Management",
    title: "Forwarding emails to your project",
    content: "Each project has a unique email address. Click \"Copy Email Address\" on the project page to copy it. Forward any HTML email to this address, and it will automatically appear in your project as a new version. This works with emails from any email service provider or ESP.",
  },
  {
    id: "version-history",
    category: "Email Management",
    title: "How version history works",
    content: "Every email you forward creates a new version automatically. Click \"View Version History\" to see all previous versions with timestamps. You can view any past version, but commenting is only enabled on the latest version. Older versions are marked as \"Read-only\".",
  },
  {
    id: "element-comments",
    category: "Commenting & Feedback",
    title: "Adding comments to specific elements",
    content: "Click on any element in the email preview (images, text, buttons, etc.) to add a contextual comment. A popover will appear where you can type your feedback. These element-specific comments help reviewers understand exactly which part of the email you're referring to.",
  },
  {
    id: "general-comments",
    category: "Commenting & Feedback",
    title: "Adding general comments",
    content: "Use the comment input at the bottom of the Comments Panel to add general feedback that isn't tied to a specific element. This is useful for overall impressions, approval status, or feedback about the email as a whole.",
  },
  {
    id: "delete-comments",
    category: "Commenting & Feedback",
    title: "Deleting your comments",
    content: "Hover over any comment you've written to reveal the delete button (trash icon). Click it to remove your comment. Note that you can only delete your own comments, not comments from other reviewers.",
  },
  {
    id: "share-links",
    category: "Sharing & Collaboration",
    title: "Sharing projects with stakeholders",
    content: "Click the \"Share\" button on any project to generate a shareable link. Copy this link and send it to clients, team members, or stakeholders. They'll be able to view the email preview and leave comments without needing a GreenLight account.",
  },
  {
    id: "password-protection",
    category: "Sharing & Collaboration",
    title: "Password protecting share links",
    content: "In the Share modal, toggle on \"Password Protection\" to require a password for access. Enter your desired password and share it separately with your reviewers. You can update or remove the password at any time from the same modal.",
  },
  {
    id: "reviewer-experience",
    category: "Sharing & Collaboration",
    title: "How reviewers access shared projects",
    content: "When someone opens a share link, they'll be prompted to enter their name (and password if protected). After entering their details, they can view the email and add comments. Their name will appear on any comments they leave for easy identification.",
  },
  {
    id: "rename-project",
    category: "Project Management",
    title: "Renaming projects",
    content: "Click on the project name at the top of the project page (or use the three-dot menu on the dashboard) to rename it. Enter the new name and save. This won't affect the project's email address or any existing share links.",
  },
  {
    id: "archive-restore",
    category: "Project Management",
    title: "Archiving and restoring projects",
    content: "Use the three-dot menu on any project card and select \"Archive\" to move it to the archived section. Archived projects don't count toward your project limit. To restore, find the project in the \"Archived Projects\" section and click \"Restore\" from its menu.",
  },
  {
    id: "delete-project",
    category: "Project Management",
    title: "Deleting projects permanently",
    content: "Only archived projects can be deleted. Find the project in the \"Archived Projects\" section, click the three-dot menu, and select \"Delete\". This action is permanent and cannot be undone—all versions and comments will be removed.",
  },
  {
    id: "plan-limits",
    category: "Subscription & Billing",
    title: "Understanding plan limits",
    content: "Each plan has a limit on active projects: Free allows 1, Starter allows 3, Pro allows 10, and Unlimited has no limit. Archived projects don't count toward your limit. Your current usage is shown on the dashboard below your greeting.",
  },
  {
    id: "upgrading",
    category: "Subscription & Billing",
    title: "Upgrading your plan",
    content: "Click \"Plans\" in the avatar dropdown menu to view available plans and pricing. Select a plan and complete the checkout process with Stripe. Your new limits will be available immediately after payment is confirmed.",
  },
  {
    id: "manage-billing",
    category: "Subscription & Billing",
    title: "Managing billing and subscription",
    content: "If you're on a paid plan, click \"Billing\" in the avatar dropdown to access the Stripe Customer Portal. There you can update payment methods, view invoices, change your plan, or cancel your subscription.",
  },
];

const Help = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const { subscribed, openCustomerPortal } = useSubscription();

  const handleContactUsClick = () => {
    const element = document.getElementById('contact-us');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const getUserInitials = () => {
    const firstName = user?.user_metadata?.first_name || "";
    const lastName = user?.user_metadata?.last_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth", { replace: true });
  };
  const [searchQuery, setSearchQuery] = useState("");
  
  // User profile state
  const [userProfile, setUserProfile] = useState<{ firstName: string; lastName: string } | null>(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    subject: "",
    message: "",
  });
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .maybeSingle();

      if (data) {
        setUserProfile({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
        });
      }
    };

    fetchProfile();
  }, [user]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = contactSchema.safeParse(contactForm);
    
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as string] = err.message;
        }
      });
      setContactErrors(errors);
      return;
    }
    
    const fullName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ") || "Unknown User";
    const email = user?.email || "";
    
    setContactErrors({});
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: fullName,
          email,
          subject: contactForm.subject,
          message: contactForm.message,
        },
      });
      
      if (error) throw error;
      
      setContactForm({ subject: "", message: "" });
      toast({
        title: "Message sent",
        description: "We'll get back to you as soon as possible.",
      });
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: "Failed to send message",
        description: "Please try again later or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return helpTopics;
    
    const query = searchQuery.toLowerCase();
    return helpTopics.filter(
      (topic) =>
        topic.title.toLowerCase().includes(query) ||
        topic.content.toLowerCase().includes(query) ||
        topic.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-background grain">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2"
            >
              <GreenLightLogo width={190} animate={false} />
            </button>

            <div className="flex items-center gap-6">
              {/* Help Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                    How to Use
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                    Provide Feedback
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Avatar Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="bg-foreground text-background font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Plans
                    {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                  </DropdownMenuItem>
                  {subscribed && (
                    <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-3xl font-bold text-foreground">
            Support
          </h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>How to Use GreenLight</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search Input */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search help topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Results Count */}
            <p className="text-sm text-muted-foreground mb-4">
              {filteredTopics.length === helpTopics.length
                ? `Showing all ${helpTopics.length} topics`
                : `Showing ${filteredTopics.length} of ${helpTopics.length} topics`}
            </p>

            {filteredTopics.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                No topics found matching "{searchQuery}"
              </p>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {filteredTopics.map((topic) => (
                  <AccordionItem key={topic.id} value={topic.id}>
                    <AccordionTrigger>{topic.title}</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground">{topic.content}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>

        {/* Contact Support Form */}
        <Card id="contact-us" className="mt-8">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Contact Us</CardTitle>
            </div>
            <CardDescription>
              Can't find what you're looking for? Send us a message and we'll get back to you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  placeholder="What can we help you with?"
                />
                {contactErrors.subject && (
                  <p className="text-sm text-destructive">{contactErrors.subject}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Describe your issue or question..."
                  rows={5}
                />
                {contactErrors.message && (
                  <p className="text-sm text-destructive">{contactErrors.message}</p>
                )}
              </div>
              
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Help;
